import * as z from "zod";
import { userFacingError } from "../authorization";
import { ensureSlug } from "../connectorSlug";
import { zql } from "../generatedDrizzleSchema";
import { insertWorkflowSkeleton } from "./createWorkflow";
import { mutator } from "./mutator";

export const DERIVED_WORKFLOW_PLACEHOLDER_PROMPT =
  "Building workflow from chat…";

export const deriveWorkflowFromChatSchema = z.object({
  personalChatId: z.string(),
  workflowId: z.string(),
  branchId: z.string(),
  commitId: z.string(),
  chatId: z.string(),
  userEntryId: z.string(),
  spaceId: z.string(),
});

export const deriveWorkflowFromChat = mutator({
  schema: deriveWorkflowFromChatSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      personalChatId,
      workflowId,
      space,
      branchId,
      commitId,
      chatId,
      userEntryId,
    },
  }) => {
    const { userId } = ctx;
    const [personalChat] = await tx.run(
      zql.personalChats.where("id", personalChatId).where("userId", userId)
    );
    if (!personalChat) throw userFacingError("Personal chat not found");
    if (personalChat.derivedWorkflowId) {
      throw userFacingError("Chat already has a workflow");
    }

    await insertWorkflowSkeleton({
      tx,
      userId,
      tenantId: ctx.tenantId,
      workflowId,
      spaceId: space.id,
      branchId,
      commitId,
      chatId,
      userEntryId,
      name: "Untitled",
      prompt: DERIVED_WORKFLOW_PLACEHOLDER_PROMPT,
    });

    await tx.mutate.personalChats.update({
      id: personalChatId,
      derivedWorkflowId: workflowId,
    });

    const associations = await tx.run(
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("refId", personalChatId)
    );
    for (const assoc of associations) {
      const [app] = await tx.run(
        zql.connectedApps.where("id", assoc.connectedAppId)
      );
      if (!app) continue;
      await ensureSlug(
        tx,
        workflowId,
        app.id,
        app.identifier,
        app.name,
        ctx.tenantId
      );
    }
  },
});
